import { postActivityEvents } from './api';
import { getActivitySessionId } from './sessionStorage';
import type { ActivityEventInput, ClientActivityEventType } from './types';

const FLUSH_DELAY_MS = 10_000;
const MAX_BATCH_SIZE = 20;
const MAX_QUEUE_SIZE = 200;

const queue: ActivityEventInput[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;
let isPaused = false;

const scheduleFlush = (): void => {
  if (flushTimer || isPaused) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushActivityQueue();
  }, FLUSH_DELAY_MS);
};

const pauseFlushing = (retryAfterSec: number): void => {
  isPaused = true;
  if (retryTimer) {
    clearTimeout(retryTimer);
  }
  retryTimer = setTimeout(() => {
    isPaused = false;
    retryTimer = null;
    scheduleFlush();
  }, retryAfterSec * 1000);
};

export const trackActivity = (
  eventType: ClientActivityEventType,
  extra?: Partial<ActivityEventInput>
): void => {
  if (typeof window === 'undefined') return;

  queue.push({
    eventType,
    occurredAt: new Date().toISOString(),
    routePath: window.location.pathname,
    ...extra,
  });

  if (queue.length > MAX_QUEUE_SIZE) {
    queue.splice(0, queue.length - MAX_QUEUE_SIZE);
  }

  if (queue.length >= 10) {
    void flushActivityQueue();
    return;
  }

  scheduleFlush();
};

export const flushActivityQueue = async (): Promise<void> => {
  if (isFlushing || isPaused || queue.length === 0) return;

  isFlushing = true;
  const batch = queue.splice(0, MAX_BATCH_SIZE);

  try {
    const sessionId = getActivitySessionId();
    const response = await postActivityEvents({
      sessionId: sessionId ?? undefined,
      events: batch,
    });

    if (response.errors?.length) {
      console.error('Activity events rejected:', response.errors);
    }
  } catch (error) {
    const status = (error as { response?: { status?: number; data?: { retryAfterSec?: number } } })
      ?.response?.status;
    const retryAfterSec =
      (error as { response?: { data?: { retryAfterSec?: number } } })?.response?.data
        ?.retryAfterSec ?? 60;

    if (status === 429) {
      queue.unshift(...batch);
      pauseFlushing(retryAfterSec);
      return;
    }

    if (status === 401) {
      return;
    }

    console.error('Failed to flush activity queue:', error);
  } finally {
    isFlushing = false;
    if (queue.length > 0 && !isPaused) {
      scheduleFlush();
    }
  }
};

export const clearActivityQueue = (): void => {
  queue.length = 0;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  isPaused = false;
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (queue.length === 0) return;
    void flushActivityQueue();
  });
}
